import { useState } from 'react';
import { api } from '../api';
import { fmtMoney, fmtShortDate, flagReasonLabel } from '../lib/format';

interface FlaggedItem {
  id: string;
  occurred_at: string;
  amount_cents: number;
  merchant: string | null;
  description: string | null;
  category_slug: string;
  flag_reasons: string | null;
  account_name: string;
}

export function FlaggedList({
  items,
  onChange,
  categoryOptions,
}: {
  items: FlaggedItem[];
  onChange: () => void;
  categoryOptions: Array<{ slug: string; name: string }>;
}) {
  if (items.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
        Nothing flagged for this day — all charges look normal and categorized.
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-1">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-amber-900">
          {items.length} charge{items.length === 1 ? '' : 's'} to review
        </h2>
      </div>
      <div className="bg-white rounded-lg border border-amber-100 divide-y divide-slate-100">
        {items.map(item => (
          <FlaggedRow key={item.id} item={item} onChange={onChange} categoryOptions={categoryOptions} />
        ))}
      </div>
    </div>
  );
}

function FlaggedRow({
  item,
  onChange,
  categoryOptions,
}: {
  item: FlaggedItem;
  onChange: () => void;
  categoryOptions: Array<{ slug: string; name: string }>;
}) {
  const [busy, setBusy] = useState(false);
  const reasons: string[] = (() => { try { return JSON.parse(item.flag_reasons ?? '[]'); } catch { return []; } })();

  async function ack() {
    setBusy(true);
    try { await api.ack(item.id); onChange(); } finally { setBusy(false); }
  }

  async function recategorize(slug: string) {
    setBusy(true);
    try {
      await api.categorize(item.id, slug);
      await api.ack(item.id);
      onChange();
    } finally { setBusy(false); }
  }

  return (
    <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-medium truncate">{item.merchant ?? item.description ?? 'Unknown merchant'}</span>
          <span className="text-xs text-slate-500 whitespace-nowrap">{fmtShortDate(item.occurred_at)} · {item.account_name}</span>
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {reasons.map(r => (
            <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
              {flagReasonLabel(r)}
            </span>
          ))}
        </div>
      </div>
      <div className={`tnum font-semibold shrink-0 ${item.amount_cents < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
        {fmtMoney(item.amount_cents, { sign: true })}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <select
          defaultValue={item.category_slug}
          disabled={busy}
          onChange={e => recategorize(e.target.value)}
          className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
        >
          {categoryOptions.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <button
          onClick={ack}
          disabled={busy}
          className="text-sm px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
        >
          Looks fine
        </button>
      </div>
    </div>
  );
}
